<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { object, string } from 'zod'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type { ManagementSuggestionValues } from '@/types/managementSuggestionTypes'

import ButtonPrimary from '@/components/common/ui/buttons/ButtonPrimary.vue'
import ButtonSecondary from '@/components/common/ui/buttons/ButtonSecondary.vue'
import SingleSelect from '@/components/common/ui/inputs/SingleSelect.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import { translationFunctionCommon, translationFunctionDefault } from '@/utilities/translations'

interface ManagementSuggestionEditFormProps {
  roleOptions: DropdownOption<null | string>[]
  salutationOptions: DropdownOption<null | string>[]
  values: ManagementSuggestionValues
}

interface ManagementSuggestionFormValues {
  first_name: string
  last_name: string
  position?: string | undefined
  role?: null | string | undefined
  salutation?: null | string | undefined
}

const props = defineProps<ManagementSuggestionEditFormProps>()
const emit = defineEmits<{ cancel: []; save: [values: ManagementSuggestionValues] }>()

const tfCommon = translationFunctionCommon()
const tfDefault = translationFunctionDefault()

const requiredMessage = tfDefault('editRelationGroup.requiredInput')

const { handleSubmit } = useForm<ManagementSuggestionFormValues>({
  initialValues: { ...props.values },
  validationSchema: toTypedSchema(
    object({
      first_name: string({ required_error: requiredMessage }).trim().min(1, requiredMessage),
      last_name: string({ required_error: requiredMessage }).trim().min(1, requiredMessage),
      position: string().nullable().optional(),
      role: string().nullable().optional(),
      salutation: string().nullable().optional(),
    }),
  ),
})

const onCancel = (): void => {
  emit('cancel')
}

const onSave = handleSubmit((formValues) => {
  emit('save', {
    first_name: formValues.first_name,
    last_name: formValues.last_name,
    position: formValues.position ?? '',
    role: formValues.role ?? null,
    salutation: formValues.salutation ?? null,
  })
})
</script>

<template>
  <div class="flex flex-col gap-4 rounded-lg bg-white p-4">
    <SingleSelect
      :label="tfDefault('editPerson.generalDataAcademicTitle')"
      name="salutation"
      on-white-bg
      :options="[salutationOptions]"
    />
    <TextInput :label="tfDefault('editPerson.generalDataFirstName')" name="first_name" required />
    <TextInput :label="tfDefault('editPerson.generalDataLastName')" name="last_name" required />
    <SingleSelect
      :label="tfDefault('editOrganization.contactRole')"
      name="role"
      on-white-bg
      :options="[roleOptions]"
      searchable
    />
    <TextInput :label="tfDefault('editOrganization.contactDescription')" name="position" />
    <div class="flex justify-end gap-3">
      <ButtonSecondary :label="tfCommon('button.cancel')" @click="onCancel" />
      <ButtonPrimary :label="tfCommon('button.save')" small @click="onSave" />
    </div>
  </div>
</template>
